import { lookupCciSku } from './lookupCciSku';
import { generateHwBom } from './generateBom';
import { getNetworkOption } from './networkPorts';
import { isDualCpuFlashQuote } from './flashNodeModel';
import { ssdMediaType } from './ssdMediaType';
import { DRIVE_TYPES } from './partNumbers';

// CONF-93 closed all three gaps this comment used to record: config accepts the
// network option ids, director NIC cards are resolved from the selected option
// rather than hardcoded to 'OCP', and the $0.00 NIC pricing is gone — the NDR400
// pair was priced (5dfa5767) and 400GbE resolves onto the already-priced
// Broadcom 200GbE pair (622af740). Every NIC part now carries a real price in
// every quarter, so no bundle rollup is short.
//
// What remains are coverage gaps in cciSkuMapping.json — data, not logic. A
// lookup miss is not an error: resolveCciQuotePath degrades to 'cci_fallback',
// the customer gets an ordinary quote, and the reason reaches the audit log and
// nowhere else. Each of these is therefore silent at the point of sale:
//   - Directors: bundles exist at 1.92TB only, but capabilities.veloSsdCapacities
//     offers 1.92/3.84/7.68. Picking either larger director SSD drops the whole
//     CCI bundle.
//   - AFA: bundles start at 7.68TB, but productMappings.afaSsd offers 3.84.
//   - QLC: no bundle covers it. Deliberate today — see isQlcDrive below — but
//     it is a live gap now the QLC drives ship (2026-Q4). CONF-224.
//   - 200GbE books the slot-1 bundle through `cciVariant`, so the description it
//     prints still reads "DP 100GbE". BOM-correct, wording wrong; Chris/Petros
//     to confirm or issue a real 200GbE row.
//
// Storage and AFA nodes always take OCP+PCIe; directors follow the option.
const NIC_CARDS_BY_NODE_TYPE = {
  storage: 'OCP+PCIe',
  afa: 'OCP+PCIe',
};

// JBOD enclosures have PNs like VCH-5000-J78-1872, VCH-5000-J108-3240.
// The all-flash variants end in -0 and are excluded (they carry no HDDs).
const isJbodPn = (pn) => /^VCH-5000-J\d+-[1-9]/.test(pn);

// cciSkuMapping.json has no drive-type dimension, and vendorRegistry exposes TLC
// and QLC drives at identical capacities (e.g. VCH-NVME-30.7 and
// VCH-NVME-30.7-QLC are both 30.72TB). Without this guard a QLC config would
// silently resolve to the TLC bundle SKU. No CCI SKU covers QLC today, so any
// QLC drive is treated as a lookup miss and takes the cci_fallback path.
//
// CONF-224: QLC-ness is the part's registry entry (`mediaType: 'qlc'`), read
// through the same resolver the metrics layer uses (CONF-221), not a
// part-number suffix — one definition of "is this drive QLC". The vendor is
// config.hardwareVendor, the field the AIC gate below already checks and the
// one metrics/index.ts passes; the `vendor` argument is not used because its
// callers and tests do not guarantee it carries an id. Parity with the old
// /-QLC$/i match over every registered SSD part is pinned in
// generateCciBom.test.js.
const isQlcDrive = (vendorId, partNumber) =>
  ssdMediaType(vendorId, partNumber) === DRIVE_TYPES.SSD_QLC;

// @param {PriceResolver} prices - Resolved price source. REQUIRED, no default:
//   see src/pricing/priceResolver.ts for why omission must throw. MUST be built over the
//   same price book the caller's pricingData was calculated with. Most generateHwBom rows
//   read their extended total straight out of pricingData, but the storage/AFA network-card
//   rows recompute theirs from the unit price — so a mismatched resolver leaves those two
//   rows priced off the wrong book and the CCI bundle silently short of the configurator's
//   hardware total.
export function generateCciBom(config, productDb, metrics, pricingData, vendor, prices) {
  if (typeof prices?.of !== 'function') {
    throw new TypeError('generateCciBom requires a PriceResolver.');
  }
  if (config.hardwareVendor !== 'AIC') {
    return { success: false, reason: 'CCI BOM is only available for AIC configurations.' };
  }
  // CONF-210: cciSkuMapping.json has no OCP + 2x PCIe row, and the AFA lookup
  // is keyed on capacity and NIC variant only, so a Dual CPU config would
  // otherwise match the single-CPU bundle. Refuse before any lookup.
  if (isDualCpuFlashQuote(config)) {
    return { success: false, reason: 'No CCI bundle covers the Dual CPU flash node (VCH-5500-F1N).' };
  }

  // One of the CONF-93 network option ids. `cciVariant` lets an option book a
  // bundle filed under a different name: 200GbE is the same card and node build
  // as slot 1, which the CCI sheet files under "100GbE" (2026-09-17).
  const networkOption = getNetworkOption(vendor?.capabilities, config.interfaceOption);
  const nicVariant = networkOption?.cciVariant ?? config.interfaceOption;
  const directorNicCards = networkOption?.directorNicCards ?? 'OCP';
  const hwRows = generateHwBom(config, productDb, metrics, pricingData, vendor, prices);

  const lookupAttempts = [];

  if (config.veloCount > 0) {
    lookupAttempts.push({
      label: 'directors',
      nodeType: 'director',
      nodeTag: 'D',
      count: config.veloCount,
      ssdCapacityTb: config.veloSsdCapacity,
      nicCards: directorNicCards,
    });
  }
  if (config.hybridEnabled) {
    lookupAttempts.push({
      label: 'hybrid storage nodes',
      nodeType: 'storage',
      nodeTag: 'S',
      count: config.hybridNodeCount,
      ssdCapacityTb: config.hybridVpodSsdCapacity,
      ssdPartNumber: config.hybridSsdPartNumber,
      nicCards: NIC_CARDS_BY_NODE_TYPE.storage,
    });
  }
  if (config.afaEnabled) {
    lookupAttempts.push({
      label: 'AFA nodes',
      nodeType: 'afa',
      nodeTag: 'F',
      count: config.afaNodeCount,
      ssdCapacityTb: config.afaVpodSsdCapacity,
      ssdPartNumber: config.afaSsdPartNumber,
      nicCards: NIC_CARDS_BY_NODE_TYPE.afa,
    });
  }

  const resolved = [];
  const misses = [];

  for (const attempt of lookupAttempts) {
    const isQlc = isQlcDrive(config.hardwareVendor, attempt.ssdPartNumber);
    const entry = isQlc
      ? null
      : lookupCciSku(attempt.nodeType, attempt.ssdCapacityTb, nicVariant, attempt.nicCards);
    if (entry) {
      resolved.push({ entry, attempt });
    } else {
      misses.push({
        label: attempt.label,
        nodeType: attempt.nodeType,
        ssdCapacityTb: attempt.ssdCapacityTb,
        nicVariant,
        nicCards: attempt.nicCards,
        ...(isQlc ? { driveType: 'QLC', ssdPartNumber: attempt.ssdPartNumber } : {}),
      });
    }
  }

  if (misses.length > 0) {
    const detail = misses
      .map(m => `${m.label} (${m.ssdCapacityTb}TB${m.driveType ? ` ${m.driveType}` : ''} / ${m.nicVariant})`)
      .join(', ');
    return { success: false, reason: `No CCI SKU covers: ${detail}.`, misses };
  }

  const rows = [];

  for (const { entry, attempt } of resolved) {
    const groupTotal = hwRows
      .filter(r => r.node === attempt.nodeTag && !isJbodPn(r.partNumber))
      .reduce((sum, r) => sum + parseFloat(r.totalCost || 0), 0);

    const unitCost = groupTotal / attempt.count;
    rows.push({
      cciSku: entry.cciSku,
      description: entry.description,
      nodeType: entry.nodeType,
      quantity: attempt.count,
      unitCost,
      totalCost: unitCost * attempt.count,
    });
  }

  // JBOD pass-through: CCI quotes list enclosures as line items, not bundled
  const jbodRows = hwRows.filter(r => r.node === 'S' && isJbodPn(r.partNumber));
  for (const jbodRow of jbodRows) {
    const unitCost = parseFloat(jbodRow.totalCost) / jbodRow.quantity;
    rows.push({
      cciSku: jbodRow.partNumber,
      description: jbodRow.item || jbodRow.partNumber,
      nodeType: 'jbod',
      quantity: jbodRow.quantity,
      unitCost,
      totalCost: parseFloat(jbodRow.totalCost),
    });
  }

  return { success: true, rows };
}
